import React, {useCallback, useEffect, useState} from 'react'
import {Button} from '@mui/material'
import {useTableParams} from '../../core/hooks/table-params'
import Loader from '../../core/components/Loader'
import {IColumn, Table} from '../../core/components/Table'
import {Pagination} from '../../core/components/Pagination'
import {IVoucher} from '../../core/interfaces'
import {getAllVouchers} from '../../services/vouchers'
import {VoucherModal} from './VoucherModal'

function isActive(expireDate: string): boolean {
    return new Date(expireDate) > new Date()
}

function formatExpireDate(expireDate: string): string {
    return new Date(expireDate).toLocaleDateString('ro', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export const VouchersListPage: React.FC = (): JSX.Element => {
    const {page, setPage, size, setSize, totalElements, setTotalElements, sortBy, setSortBy} =
        useTableParams({defaultSortBy: 'createdAt'})

    const [loading, setLoading] = useState<boolean>(false)
    const [vouchers, setVouchers] = useState<IVoucher[]>([])
    const [showModal, setShowModal] = useState<boolean>(false)
    const [editVoucher, setEditVoucher] = useState<IVoucher | undefined>(undefined)

    const getData = useCallback(async () => {
        try {
            setLoading(true)
            const response = await getAllVouchers({page, size, sortBy})
            setTotalElements(response.data.totalElements)
            setVouchers(response.data.content)
            setPage(page + 1)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [page, size, sortBy])

    useEffect(() => {
        getData()
    }, [page, size, sortBy])

    const handleSortBy = (sort: string) => {
        setSortBy(sort === sortBy ? 'createdAt' : sort)
    }

    const openCreate = () => {
        setEditVoucher(undefined)
        setShowModal(true)
    }

    const openEdit = (voucher: IVoucher) => {
        setEditVoucher(voucher)
        setShowModal(true)
    }

    const columns: IColumn[] = [
        {title: 'Code', accessor: 'code', sortable: true, searchable: false},
        {
            title: 'Discount',
            accessor: '',
            render: (row: IVoucher) =>
                row.discountPercentage > 0
                    ? <span>{row.discountPercentage}%</span>
                    : <span>{row.discount} RON</span>,
        },
        {
            title: 'Expires',
            accessor: '',
            render: (row: IVoucher) => <span>{formatExpireDate(row.expireDate)}</span>,
        },
        {
            title: 'Status',
            accessor: '',
            render: (row: IVoucher) => (
                <span style={{
                    fontWeight: 600,
                    color: isActive(row.expireDate) ? '#388e3c' : '#9e9e9e',
                }}>
                    {isActive(row.expireDate) ? 'Active' : 'Expired'}
                </span>
            ),
        },
        {
            title: 'Assigned Creator',
            accessor: '',
            render: (row: IVoucher) =>
                row.userId
                    ? <span>{row.userDisplayName} <span style={{color: '#888'}}>({row.userEmail})</span></span>
                    : <span style={{color: '#bbb'}}>—</span>,
        },
        {
            title: 'Usages',
            accessor: '',
            render: (row: IVoucher) => <span>{row.usageCount}</span>,
        },
        {
            title: 'Actions',
            accessor: '',
            render: (row: IVoucher) => (
                <Button className="btn btn-secondary" onClick={() => openEdit(row)}>
                    Edit
                </Button>
            ),
        },
    ]

    return (
        <>
            <div style={{padding: 20, backgroundColor: '#F8F8F8', position: 'relative'}}>
                {loading && <Loader />}
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
                    <h2>Vouchers</h2>
                    <Button variant="contained" onClick={openCreate}>New Voucher</Button>
                </div>
                <div className="overflow-x-auto">
                    <Table
                        handleSearch={() => {}}
                        isCheck={[]}
                        columns={columns}
                        handleSortBy={handleSortBy}
                        data={vouchers}
                    />
                </div>
                <Pagination
                    size={size}
                    totalElements={totalElements}
                    setSize={setSize}
                    page={page + 1}
                    changePage={newPage => setPage(newPage)}
                />
            </div>

            <VoucherModal
                show={showModal}
                onHide={() => setShowModal(false)}
                editVoucher={editVoucher}
                onSaved={() => {
                    setShowModal(false)
                    getData()
                }}
            />
        </>
    )
}
